import {
  SCENE_LIGHT_AMBIENT_MULT,
  SCENE_LIGHT_BASE_INTENSITY,
  SCENE_LIGHT_FILL_MULT,
  SCENE_LIGHT_FOCUS_MULT,
  SCENE_LIGHT_HEMISPHERE_MULT,
  SCENE_LIGHT_KEY_MULT,
  SCENE_LIGHT_RIM_MULT,
  SCENE_SHADOW_RADIUS,
  SCENE_SHADOW_CAMERA_HALF_EXTENT,
} from '@constants'
import { useAppStore } from '@store'
import { LightProps } from '@types'
import { useRef } from 'react'
import { Vector3, Quaternion } from 'three'
import isItem from '@utils/sceneObjects/assertions/isItem'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import { useFrame } from '@react-three/fiber'

interface AmbientLight3DProps extends Partial<LightProps> {
  id?: string
  color?: string
  intensity?: number
}

/** Luz más vertical → sombra más corta y pegada al objeto. */
const KEY_LIGHT_POSITION = new Vector3(3200, 16500, 2200)
const FILL_LIGHT_POSITION = new Vector3(-7500, 9000, 3500)
const RIM_LIGHT_POSITION = new Vector3(0, 7000, -11000)

export function AmbientLight3D(props?: AmbientLight3DProps) {
  const lightRef = useRef(null)
  const keyLightRef = useRef<any>(null)
  const focusLightRef = useRef<any>(null)
  const prefsColor = useAppStore.use.prefs()?.AMBIENTECOLOR ?? 'white'
  const prefsIntensity = useAppStore.use.prefs()?.AMBIENTEINTENSIDAD
  const items = useAppStore.use.items()
  const currentObjectID = useAppStore.use.currentObjectID()
  const currentLayer = useAppStore.use.currentLayer()
  const selectedItems = useAppStore.use.layers()[currentLayer]?.selected

  const hasWalls = Array.from(items.values()).some((item) => item.type === 'walls')

  const color = props?.color ?? prefsColor
  const rawPrefs = parseInt(prefsIntensity, 10)
  const prefsBase = Number.isFinite(rawPrefs) && rawPrefs > 0 ? rawPrefs : 1
  const baseIntensity = props?.intensity ?? prefsBase * SCENE_LIGHT_BASE_INTENSITY
  const sceneIntensity = baseIntensity * (hasWalls ? 0.9 : 1)

  const key = props?.id
    ? `ambient-light-${props.id}-${color}-${sceneIntensity}`
    : `scene-ambient-light-${color}-${sceneIntensity}`

  const targetItemId =
    currentObjectID || (selectedItems.size > 0 ? Array.from(selectedItems)[0] : null)

  useFrame(() => {
    if (props?.id) return

    const focusCenter = new Vector3(0, 400, 0)
    const targetItem = targetItemId ? items.get(targetItemId) : null

    if (targetItem && (isItem(targetItem) || isLinkedItem(targetItem)) && targetItem.matrix) {
      const position = new Vector3()
      const quaternion = new Quaternion()
      const scale = new Vector3()
      targetItem.matrix.decompose(position, quaternion, scale)

      const itemHeight = targetItem.height || 0
      focusCenter.set(position.x, position.y + itemHeight * 0.45, position.z)

      if (focusLightRef.current) {
        const lightHeight = position.y + itemHeight + 10000
        focusLightRef.current.position.set(position.x, lightHeight, position.z + 2000)
        focusLightRef.current.target.position.copy(focusCenter)
        focusLightRef.current.target.updateMatrixWorld()
      }
    } else if (focusLightRef.current) {
      focusLightRef.current.position.set(0, 10000, 2000)
      focusLightRef.current.target.position.copy(focusCenter)
      focusLightRef.current.target.updateMatrixWorld()
    }

    if (keyLightRef.current) {
      keyLightRef.current.target.position.copy(focusCenter)
      keyLightRef.current.target.updateMatrixWorld()

      const shadowCam = keyLightRef.current.shadow?.camera
      if (shadowCam) {
        const half = SCENE_SHADOW_CAMERA_HALF_EXTENT
        shadowCam.left = -half
        shadowCam.right = half
        shadowCam.top = half
        shadowCam.bottom = -half
        shadowCam.near = 10
        shadowCam.far = 28000
        shadowCam.updateProjectionMatrix()
      }
    }
  })

  return (
    <>
      {/* Relleno omnidireccional: levanta sombras sin subir la key (evita quemar claros). */}
      {!props?.id && (
        <hemisphereLight
          color='#f4f7fc'
          groundColor='#ebe4dc'
          intensity={sceneIntensity * SCENE_LIGHT_HEMISPHERE_MULT}
        />
      )}

      <ambientLight
        ref={lightRef}
        position={[0, props?.y ?? 1000, 0]}
        key={key}
        color={color}
        intensity={sceneIntensity * SCENE_LIGHT_AMBIENT_MULT}
      />

      {!props?.id && (
        <>
          {/* Key fija tipo estudio de producto */}
          <directionalLight
            ref={keyLightRef}
            position={KEY_LIGHT_POSITION}
            color={color}
            intensity={sceneIntensity * SCENE_LIGHT_KEY_MULT}
            castShadow
            shadow-mapSize-width={2048}
            shadow-mapSize-height={2048}
            shadow-radius={SCENE_SHADOW_RADIUS}
            shadow-bias={-0.00025}
            shadow-normalBias={0.02}
            shadow-camera-near={10}
            shadow-camera-far={28000}
            shadow-camera-left={-SCENE_SHADOW_CAMERA_HALF_EXTENT}
            shadow-camera-right={SCENE_SHADOW_CAMERA_HALF_EXTENT}
            shadow-camera-top={SCENE_SHADOW_CAMERA_HALF_EXTENT}
            shadow-camera-bottom={-SCENE_SHADOW_CAMERA_HALF_EXTENT}
          />

          {/* Relleno lateral sin sombra */}
          <directionalLight
            position={FILL_LIGHT_POSITION}
            intensity={sceneIntensity * SCENE_LIGHT_FILL_MULT}
            color={color}
          />

          {/* Contraluz suave para separar del fondo */}
          <directionalLight
            position={RIM_LIGHT_POSITION}
            intensity={sceneIntensity * SCENE_LIGHT_RIM_MULT}
            color='#fffaf5'
          />

          {/* Refuerzo sobre la unidad activa */}
          <directionalLight
            ref={focusLightRef}
            key={`focus-light-${targetItemId || 'default'}`}
            color={color}
            intensity={sceneIntensity * SCENE_LIGHT_FOCUS_MULT}
          />
        </>
      )}
    </>
  )
}
